import { useEffect, useState, useRef } from "react";
import "../App.css";
import "../Backgrounds.css";
import "./Home.css";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import DataPipelineVisualization from "../components/DataPipelineVisualization";

// Simulated pipeline stages for the demo
const PIPELINE_STAGES = [
  "input",
  "validate",
  "parse",
  "transform",
  "store",
  "output",
];
const ERROR_STAGES = ["input", "validate", "error", "retry"];

const DataPipelineDemo = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [processingPath, setProcessingPath] = useState("valid");
  const [retryCount, setRetryCount] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [speed, setSpeed] = useState(1000); // ms per stage
  const intervalRef = useRef(null);
  const stageIndexRef = useRef(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-play animation
  useEffect(() => {
    if (autoPlay && isProcessing) {
      const stages =
        processingPath === "valid" ? PIPELINE_STAGES : ERROR_STAGES;

      intervalRef.current = setInterval(() => {
        stageIndexRef.current++;

        if (stageIndexRef.current >= stages.length) {
          // Loop back or handle retry
          if (processingPath === "error" && retryCount < 2) {
            // Retry: go back to validate
            setRetryCount((prev) => prev + 1);
            stageIndexRef.current = 1; // Back to validate
            setCurrentStage("validate");
          } else {
            // Complete or max retries
            stageIndexRef.current = 0;
            setCurrentStage(stages[0]);
            if (processingPath === "error") {
              setRetryCount(0);
            }
          }
        } else {
          setCurrentStage(stages[stageIndexRef.current]);
        }
      }, speed);

      return () => clearInterval(intervalRef.current);
    }
  }, [autoPlay, isProcessing, processingPath, retryCount, speed]);

  const handleStart = () => {
    const stages = processingPath === "valid" ? PIPELINE_STAGES : ERROR_STAGES;
    stageIndexRef.current = 0;
    setCurrentStage(stages[0]);
    setIsProcessing(true);
    setRetryCount(0);
  };

  const handleStop = () => {
    setIsProcessing(false);
    setCurrentStage(null);
    setRetryCount(0);
    stageIndexRef.current = 0;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleStageClick = (stageId) => {
    if (isProcessing) {
      setCurrentStage(stageId);
      const stages =
        processingPath === "valid" ? PIPELINE_STAGES : ERROR_STAGES;
      stageIndexRef.current = stages.indexOf(stageId);
    }
  };

  const handleNextStage = () => {
    const stages = processingPath === "valid" ? PIPELINE_STAGES : ERROR_STAGES;
    stageIndexRef.current = (stageIndexRef.current + 1) % stages.length;
    setCurrentStage(stages[stageIndexRef.current]);
  };

  return (
    <div>
      <div className="foreground">
        <MainHeader />

        <div className="homeBackground">
          <div className="homeContentContainer">
            <div className="block">
              <h1
                style={{
                  color: "var(--font-colour-content1)",
                  textAlign: "center",
                }}
              >
                Data Pipeline Visualization
              </h1>
              <p
                style={{
                  color: "var(--font-colour-content2)",
                  textAlign: "center",
                  marginBottom: "30px",
                }}
              >
                Flow visualization system demo - grid-based nodes with bezier
                curves and loopback arcs
              </p>

              {/* Visualization */}
              <div
                style={{
                  background: "#030509",
                  borderRadius: "12px",
                  border: "1px solid #0f1218",
                  marginBottom: "30px",
                  minHeight: "320px",
                }}
              >
                <DataPipelineVisualization
                  currentStage={currentStage}
                  processingPath={processingPath}
                  isProcessing={isProcessing}
                  retryCount={retryCount}
                  onStageClick={handleStageClick}
                />
              </div>

              {/* Controls */}
              <div
                style={{
                  background: "#0a0c12",
                  borderRadius: "12px",
                  border: "1px solid #0f1218",
                  padding: "24px",
                }}
              >
                {/* Path Selection */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "10px",
                      color: "#6a7080",
                      textTransform: "uppercase",
                      letterSpacing: "0.25em",
                      marginBottom: "12px",
                    }}
                  >
                    Processing Path
                  </label>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <button
                      onClick={() => setProcessingPath("valid")}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "8px",
                        border:
                          processingPath === "valid"
                            ? "1px solid #4a9eb5"
                            : "1px solid #2a2f38",
                        background:
                          processingPath === "valid"
                            ? "rgba(74,158,181,0.1)"
                            : "transparent",
                        color:
                          processingPath === "valid" ? "#4a9eb5" : "#6a7080",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "500",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Valid Path
                    </button>
                    <button
                      onClick={() => setProcessingPath("error")}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "8px",
                        border:
                          processingPath === "error"
                            ? "1px solid #9e6b4a"
                            : "1px solid #2a2f38",
                        background:
                          processingPath === "error"
                            ? "rgba(158,107,74,0.1)"
                            : "transparent",
                        color:
                          processingPath === "error" ? "#9e6b4a" : "#6a7080",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "500",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Error Path (with Retry Loop)
                    </button>
                  </div>
                </div>

                {/* Speed Control */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "10px",
                      color: "#6a7080",
                      textTransform: "uppercase",
                      letterSpacing: "0.25em",
                      marginBottom: "12px",
                    }}
                  >
                    Animation Speed: {speed}ms
                  </label>
                  <input
                    type="range"
                    min="200"
                    max="2000"
                    step="100"
                    value={speed}
                    onChange={(e) => setSpeed(parseInt(e.target.value))}
                    style={{
                      width: "200px",
                      cursor: "pointer",
                    }}
                  />
                </div>

                {/* Auto-play Toggle */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      cursor: "pointer",
                      color: "#6a7080",
                      fontSize: "12px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={autoPlay}
                      onChange={(e) => setAutoPlay(e.target.checked)}
                    />
                    Auto-advance stages
                  </label>
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    onClick={handleStart}
                    disabled={isProcessing}
                    style={{
                      padding: "12px 24px",
                      borderRadius: "8px",
                      border: "1px solid #4a9eb5",
                      background: isProcessing
                        ? "transparent"
                        : "rgba(74,158,181,0.1)",
                      color: isProcessing ? "#2a2f38" : "#4a9eb5",
                      cursor: isProcessing ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    Start Pipeline
                  </button>
                  <button
                    onClick={handleNextStage}
                    disabled={!isProcessing || autoPlay}
                    style={{
                      padding: "12px 24px",
                      borderRadius: "8px",
                      border: "1px solid #6a7080",
                      background: "transparent",
                      color: !isProcessing || autoPlay ? "#2a2f38" : "#6a7080",
                      cursor:
                        !isProcessing || autoPlay ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    Next Stage
                  </button>
                  <button
                    onClick={handleStop}
                    disabled={!isProcessing}
                    style={{
                      padding: "12px 24px",
                      borderRadius: "8px",
                      border: "1px solid #9e6b4a",
                      background: "transparent",
                      color: !isProcessing ? "#2a2f38" : "#9e6b4a",
                      cursor: !isProcessing ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    Stop
                  </button>
                </div>
              </div>

              {/* Info Section */}
              <div
                style={{
                  marginTop: "30px",
                  padding: "20px",
                  background: "#020407",
                  borderRadius: "12px",
                  border: "1px solid #0f1218",
                }}
              >
                <h3
                  style={{
                    fontSize: "14px",
                    color: "#d9dada",
                    marginBottom: "15px",
                    fontWeight: "500",
                  }}
                >
                  Flow Visualization Rules Applied
                </h3>
                <ul
                  style={{
                    color: "#6a7080",
                    fontSize: "12px",
                    lineHeight: "1.8",
                    paddingLeft: "20px",
                  }}
                >
                  <li>
                    <strong style={{ color: "#4a9eb5" }}>
                      Grid Positioning:
                    </strong>{" "}
                    Nodes placed on col/row grid (100px × 60px)
                  </li>
                  <li>
                    <strong style={{ color: "#4a9eb5" }}>
                      Diagonal Paths:
                    </strong>{" "}
                    Bezier curves from validate → parse/error
                  </li>
                  <li>
                    <strong style={{ color: "#9e6b4a" }}>Loopback Arc:</strong>{" "}
                    Semi-circular arc from retry → validate (below nodes)
                  </li>
                  <li>
                    <strong style={{ color: "#4a9eb5" }}>Forward Lines:</strong>{" "}
                    Solid teal with animated pulses
                  </li>
                  <li>
                    <strong style={{ color: "#9e6b4a" }}>
                      Backward Lines:
                    </strong>{" "}
                    Dotted brown for loopback connections
                  </li>
                </ul>
              </div>
            </div>

            <div className="solidLine"></div>

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataPipelineDemo;
